import { UserModel, type NetworkUser, type UserUpdatePayloadModel } from '@/models/user';
import type { AcceptedInvite, InviteeInvite } from '@/models/inviteeInvite';
import type { SignInStudies } from '@/models/login';
import { signedApiClient } from '@/services/apiClient';

class UserService {

  private userEndpoint: string = '';
  initService(userEndpoint: string) {
    this.userEndpoint = userEndpoint;
  }
  async getUserDetails(id: string) {
    const uri = `${this.userEndpoint}/${id}/details`;
    const apiClient = signedApiClient('GET', uri);
    const userResponse = await apiClient.get(uri);
    const user: NetworkUser = userResponse.data;
    return UserModel.mapFromBackend(user, id);
  }

  async patchUserDetails(payload: UserUpdatePayloadModel, userHex: string) {
    const uri = `${this.userEndpoint}/${userHex}/details`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, payload);
  }

  /** The account's own pending invites, newest first. Another account's id gets 403. */
  async getInvites(userHex: string): Promise<InviteeInvite[]> {
    const uri = `${this.userEndpoint}/${userHex}/invite`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);
    return response.data;
  }

  /**
   * What the sign-in answer would say about the account's studies now, so `/study` can tell a person
   * deactivated since they signed in from one with no study at all. Another account's id gets 403.
   */
  async getSignInStudies(userHex: string): Promise<SignInStudies> {
    const uri = `${this.userEndpoint}/${userHex}/sign-in-studies`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);
    return response.data;
  }

  /** Joins the invite's study and groups with its grant (users decision 83). */
  async acceptInvite(userHex: string, inviteId: string): Promise<AcceptedInvite> {
    const uri = `${this.userEndpoint}/${userHex}/invite/${inviteId}/accept`;
    const apiClient = signedApiClient('POST', uri);
    const response = await apiClient.post(uri);
    return response.data;
  }

  /** Writes `declinedAt` only: no email to the inviter (users decision 84). Answers 204. */
  async declineInvite(userHex: string, inviteId: string) {
    const uri = `${this.userEndpoint}/${userHex}/invite/${inviteId}/decline`;
    const apiClient = signedApiClient('POST', uri);
    await apiClient.post(uri);
  }

  async registerFCMToken(token: string, userHex: string) {
    const uri = `${this.userEndpoint}/${userHex}/device`;
    const apiClient = signedApiClient('POST', uri);
    const res = await apiClient.post(uri, { token, platform: 'fcm_web' });
    // Any 2xx: the API answers 201 when it creates the device record and 200 when the token is already
    // registered, and the caller only persists the token when this returns true — so pinning the check
    // to either status alone leaves it unsaved and re-POSTs the same token on every page load.
    return res.status >= 200 && res.status < 300;
  }
}

export default new UserService();
